<script setup lang="ts">
  import { VSkeleton } from "@krainovsd/vue-ui";

  type Props = {
    // Relative per-column widths; columns grow in proportion to these so the
    // skeleton roughly matches the real table's column layout.
    columns: number[];
    rows?: number;
    headerHeight?: number;
    rowHeight?: number;
  };

  const { columns, rows = 8, headerHeight = 40, rowHeight = 48 } = defineProps<Props>();
</script>

<template>
  <div :class="$style.base" aria-hidden="true">
    <div :class="[$style.row, $style.header]" :style="{ height: `${headerHeight}px` }">
      <div
        v-for="(width, index) in columns"
        :key="`head-${index}`"
        :class="$style.cell"
        :style="{ flexGrow: width }"
      >
        <VSkeleton type="text" :animation="true" />
      </div>
    </div>
    <div
      v-for="rowIndex in rows"
      :key="rowIndex"
      :class="$style.row"
      :style="{ height: `${rowHeight}px` }"
    >
      <div
        v-for="(width, index) in columns"
        :key="`cell-${rowIndex}-${index}`"
        :class="$style.cell"
        :style="{ flexGrow: width }"
      >
        <VSkeleton type="text" :animation="true" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
  .base {
    width: 100%;

    border: 1px solid var(--ksd-divider-color);
    border-radius: var(--ksd-border-radius, 8px);
    overflow: hidden;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 16px;

    padding: 0 12px;

    border-bottom: 1px solid var(--ksd-divider-color);

    &:last-child {
      border-bottom: none;
    }
  }

  .header {
    background-color: var(--muted, var(--card));
  }

  .cell {
    flex: 1 1 0;
    min-width: 0;
  }
</style>
